class FreeKitCartOnly {
  constructor() {
    this.country = 'SA';
    this.threshold = Number(window.theme?.freeKitThreshold || 400);
    this.currencyEnglish = 'SAR';
    this.currencyArabic = '\u0631.\u0633';
    this.observer = null;
    this.bound = false;
    this.bind();
    this.observeCartDom();
    this.scheduleRefresh(0);
    this.scheduleRefresh(300);
    this.scheduleRefresh(1000);
  }

  get isArabic() {
    const routeRoot = (window.Shopify?.routes?.root || '').toLowerCase();
    const path = window.location.pathname.toLowerCase();
    const arabicDefaultCountries = ['BH', 'SA', 'KW', 'QA', 'OM'];
    const englishDefaultCountries = ['UAE', 'AE'];
    const hasEnglishPath = path === '/en' || path.startsWith('/en/') || routeRoot === '/en/';
    const hasArabicPath = path === '/ar' || path.startsWith('/ar/') || routeRoot === '/ar/';

    if (arabicDefaultCountries.includes(this.country)) {
      return !hasEnglishPath;
    }

    if (englishDefaultCountries.includes(this.country)) {
      return hasArabicPath;
    }

    return (
      (window.Shopify && Shopify.locale && Shopify.locale.toLowerCase().startsWith('ar')) ||
      (document.documentElement.lang && document.documentElement.lang.toLowerCase().startsWith('ar')) ||
      document.documentElement.dir === 'rtl' ||
      document.body.dir === 'rtl' ||
      hasArabicPath
    );
  }

  getCopy() {
    if (this.isArabic) {
      return {
        successMessage: '\u0644\u0642\u062f \u062d\u0635\u0644\u062a \u0639\u0644\u0649 \u0645\u062c\u0645\u0648\u0639\u0629 \u0645\u062c\u0627\u0646\u064a\u0629!',
        progressMessage: '\u0623\u0636\u0641 [amount] \u0644\u062a\u062d\u0635\u0644 \u0639\u0644\u0649 \u0645\u062c\u0645\u0648\u0639\u0629 \u0645\u062c\u0627\u0646\u064a\u0629!',
        kitName: '\u0645\u062c\u0645\u0648\u0639\u0629 \u0639\u064a\u0646\u0627\u062a \u0641\u0627\u062e\u0631\u0629',
        kitDescription: '\u0645\u062c\u0645\u0648\u0639\u0629 \u0645\u062e\u062a\u0627\u0631\u0629 \u0645\u0646 \u0623\u0631\u0642\u0649 \u0639\u0637\u0648\u0631\u0646\u0627'
      };
    }

    return {
      successMessage: window.theme?.kitSuccessMessage || 'You got a FREE Kit!',
      progressMessage: window.theme?.kitProgressMessage || 'Add [amount] more to get a FREE Kit!',
      kitName: window.theme?.kitName || 'Luxury Sample Kit',
      kitDescription: window.theme?.kitDescription || 'A curated collection of our finest fragrances'
    };
  }

  bind() {
    if (this.bound) return;
    this.bound = true;

    ['cart:updated', 'cart:refresh', 'cart:change', 'cart-drawer:open', 'ajaxProduct:added', 'product:added'].forEach((eventName) => {
      document.addEventListener(eventName, () => this.scheduleRefresh(100));
    });

    document.addEventListener('click', (event) => {
      if (
        event.target.closest('[name="add"]') ||
        event.target.closest('button[type="submit"]') ||
        event.target.closest('[name="plus"], [name="minus"], .quantity__button')
      ) {
        this.scheduleRefresh(300);
        this.scheduleRefresh(900);
        this.scheduleRefresh(1600);
      }
    });

    document.addEventListener('change', (event) => {
      if (event.target && event.target.name === 'updates[]') {
        this.scheduleRefresh(300);
        this.scheduleRefresh(900);
      }
    });
  }

  observeCartDom() {
    if (this.observer || !document.body) return;

    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (
            node.matches?.('.cart-free-kit, cart-drawer, #CartDrawer') ||
            node.querySelector?.('.cart-free-kit, cart-drawer, #CartDrawer')
          ) {
            this.scheduleRefresh(50);
            this.scheduleRefresh(400);
            return;
          }
        }
      }
    });

    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  scheduleRefresh(delay) {
    window.setTimeout(() => this.refresh(), delay);
  }

  async refresh() {
    const bars = document.querySelectorAll('.cart-kit-progress-bar');
    const messages = document.querySelectorAll('.cart-kit-message');
    const wrappers = document.querySelectorAll('.cart-free-kit');

    if (!bars.length && !messages.length && !wrappers.length) return;

    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      const cart = await response.json();
      this.updateUI(cart.total_price / 100);
    } catch (error) {
      console.error('Error updating free kit cart progress:', error);
    }
  }

  formatMoney(amount) {
    const formatted = amount.toLocaleString(this.isArabic ? 'ar' : 'en', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    });

    return this.isArabic ? formatted + ' ' + this.currencyArabic : this.currencyEnglish + ' ' + formatted;
  }

  updateUI(total) {
    const copy = this.getCopy();
    const bars = document.querySelectorAll('.cart-kit-progress-bar');
    const messages = document.querySelectorAll('.cart-kit-message');
    const wrappers = document.querySelectorAll('.cart-free-kit');
    const details = document.querySelectorAll('.cart-kit-details');
    const progress = Math.min((total / this.threshold) * 100, 100);
    const remaining = Math.max(this.threshold - total, 0);
    const achieved = total >= this.threshold;

    bars.forEach((bar) => {
      bar.style.width = progress + '%';
    });

    messages.forEach((message) => {
      message.textContent = achieved
        ? copy.successMessage
        : copy.progressMessage.replace('[amount]', this.formatMoney(remaining));
      message.dir = this.isArabic ? 'rtl' : 'ltr';
    });

    wrappers.forEach((wrapper) => {
      wrapper.classList.toggle('achieved', achieved);
      wrapper.dir = this.isArabic ? 'rtl' : 'ltr';
    });

    details.forEach((detail) => {
      detail.classList.toggle('show', achieved);
      const name = detail.querySelector('.cart-kit-name');
      const description = detail.querySelector('.cart-kit-description');
      if (name) name.textContent = copy.kitName;
      if (description) description.textContent = copy.kitDescription;
    });
  }
}

window.junaidFreeKitCart = window.junaidFreeKitCart || new FreeKitCartOnly();
document.addEventListener('DOMContentLoaded', () => {
  window.junaidFreeKitCart = window.junaidFreeKitCart || new FreeKitCartOnly();
  window.junaidFreeKitCart.scheduleRefresh(100);
});